import { create } from "zustand";
import { persist } from "zustand/middleware";
import { currentActorLabel, useActivityStore } from "@/store/activity.store";
import { showToastI18n } from "@/lib/toastI18n";

function newId(prefix) {
  return `${prefix}-${Date.now().toString(36)}`;
}
const useOfficeStore = create()(
  persist(
    (set) => ({
      taskPatches: {},
      approvalPatches: {},
      followUpPatches: {},
      reminderRead: {},
      escalationPatches: {},
      createdTasks: [],
      setTaskStatus: (id, status) => {
        set((s) => ({
          taskPatches: {
            ...s.taskPatches,
            [id]: { ...s.taskPatches[id], status },
          },
        }));
        useActivityStore.getState().appendTimeline({
          orderId: "ord-1001",
          actor: currentActorLabel(),
          kind: "audit",
          title: `Task ${id} \u2192 ${status}`,
        });
        showToastI18n("toasts.statusChanged", "success");
      },
      createTask: (task) => {
        const row = {
          ...task,
          ownerName: task.ownerName ?? task.assigneeName,
          dependsOnTaskIds: task.dependsOnTaskIds ?? [],
          id: newId("task"),
          createdAt: new Date().toISOString(),
        };
        set((s) => ({ createdTasks: [row, ...s.createdTasks] }));
        showToastI18n("toasts.created", "success");
        return row;
      },
      decideApproval: (id, status) => {
        set((s) => ({
          approvalPatches: {
            ...s.approvalPatches,
            [id]: { ...s.approvalPatches[id], status },
          },
        }));
        useActivityStore.getState().appendTimeline({
          orderId: "ord-1001",
          actor: currentActorLabel(),
          kind: "audit",
          title: `Approval ${id} ${status}`,
        });
        showToastI18n(
          status === "approved" ? "toasts.approved" : "toasts.statusChanged",
          "success",
        );
      },
      setFollowUpStatus: (id, status, patch) => {
        set((s) => ({
          followUpPatches: {
            ...s.followUpPatches,
            [id]: { ...s.followUpPatches[id], status, ...patch },
          },
        }));
        showToastI18n("toasts.statusChanged", "success");
      },
      attachPaymentEvidence: (id, evidence) => {
        if (!evidence) return;
        set((s) => ({
          followUpPatches: {
            ...s.followUpPatches,
            [id]: { ...s.followUpPatches[id], paymentEvidence: evidence },
          },
        }));
        showToastI18n("toasts.uploaded", "success");
      },
      markReminderRead: (id) => {
        set((s) => ({ reminderRead: { ...s.reminderRead, [id]: true } }));
        showToastI18n("toasts.updated", "info");
      },
      setEscalationStatus: (id, status) => {
        set((s) => ({
          escalationPatches: {
            ...s.escalationPatches,
            [id]: { ...s.escalationPatches[id], status },
          },
        }));
        showToastI18n("toasts.statusChanged", "success");
      },
    }),
    { name: "gps-erp-next-office" },
  ),
);
function allCompanyTasks(companyId, seed) {
  const { taskPatches, createdTasks } = useOfficeStore.getState();
  return [
    ...createdTasks,
    ...seed.filter((t) => t.companyId === companyId),
  ].map((t) => ({
    ...t,
    ...taskPatches[t.id],
  }));
}
function mergeTasks(seed, role, userName) {
  const { taskPatches, createdTasks } = useOfficeStore.getState();
  const all = [...createdTasks, ...seed].map((t) => ({
    ...t,
    ...taskPatches[t.id],
  }));
  return all.filter((t) => {
    if (role === "group_admin") return true;
    if (t.assigneeRole === role) return true;
    if (userName && t.assigneeName === userName) return true;
    return false;
  });
}
function mergeApprovals(seed) {
  const patches = useOfficeStore.getState().approvalPatches;
  return seed.map((a) => ({ ...a, ...patches[a.id] }));
}
function mergeFollowUps(seed) {
  const patches = useOfficeStore.getState().followUpPatches;
  return seed.map((f) => ({ ...f, ...patches[f.id] }));
}
function mergeReminders(seed) {
  const read = useOfficeStore.getState().reminderRead;
  return seed.map((r) => ({ ...r, read: read[r.id] ?? r.read }));
}
function mergeEscalations(seed) {
  const patches = useOfficeStore.getState().escalationPatches;
  return seed.map((e) => ({ ...e, ...patches[e.id] }));
}
function unreadReminderCount(companyId, seed) {
  return mergeReminders(seed).filter(
    (r) => r.companyId === companyId && !r.read,
  ).length;
}
export {
  allCompanyTasks,
  mergeApprovals,
  mergeEscalations,
  mergeFollowUps,
  mergeReminders,
  mergeTasks,
  unreadReminderCount,
  useOfficeStore,
};
